import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { TabView, TabPanel } from 'primereact/tabview';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useIntl } from 'react-intl';
import { useImportWorkoutCycleAI } from 'hooks/coach/useImportWorkoutCycleAI';
import { useQuickCreateExercise } from 'hooks/coach/useQuickCreateExercise';
import { useExercisesStore } from 'stores/useExercisesStore';
import CycleDayCard from './import-cycle/CycleDayCard';

interface Props {
  visible: boolean;
  onHide: () => void;
  onCreated: (result: { cycleId: number; workoutTemplateIds: number[] }) => void;
}

const ACCEPTED_MIME_TYPES = '.jpg,.jpeg,.png,.webp,.xlsx,.csv,image/*';
const MAX_FILE_SIZE_MB = 10;

export default function ImportWorkoutCycleAIDialog({ visible, onHide, onCreated }: Props) {
  const intl = useIntl();
  const {
    parsing,
    committing,
    preview,
    parseFile,
    parseText,
    updateCycleName,
    updateDurationWeeks,
    updateDayName,
    removeDay,
    resolveExercise,
    commit,
    reset
  } = useImportWorkoutCycleAI();
  const { createExercise } = useQuickCreateExercise();
  // Seleccionamos `exercises` directamente (referencia estable). El `.map`
  // a {id,name} se memoiza para evitar crear un array nuevo en cada render —
  // un selector tipo `(s) => s.getExercisesForDropdown()` causa infinite loop
  // porque Zustand compara por referencia y el array nuevo siempre difiere.
  const exercisesFromStore = useExercisesStore((s) => s.exercises);
  const loadExercises = useExercisesStore((s) => s.loadExercises);
  const catalogExercises = useMemo(
    () => exercisesFromStore.map((ex) => ({ id: ex.id, name: ex.name })),
    [exercisesFromStore]
  );
  const [activeTab, setActiveTab] = useState<number>(0);
  const [text, setText] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Si el catalogo aun no esta cargado en el store, lo cargamos al abrir
  // el dialog (los dropdowns de match unresolved lo necesitan).
  useEffect(() => {
    if (visible && catalogExercises.length === 0) {
      void loadExercises();
    }
  }, [visible, catalogExercises.length, loadExercises]);

  const resetLocal = () => {
    setText('');
    setSelectedFile(null);
    setActiveTab(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleHide = () => {
    if (parsing || committing) return;
    resetLocal();
    reset();
    onHide();
  };

  const handleFilePicked = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleParseFile = async () => {
    if (!selectedFile) return;
    await parseFile(selectedFile);
  };

  const handleParseText = async () => {
    await parseText(text);
  };

  const handleCommit = async () => {
    const result = await commit();
    if (result) {
      resetLocal();
      onCreated(result);
      onHide();
    }
  };

  const handleCreateExerciseFromCard = async (dayIdx: number, exerciseInternalId: string | number, query: string) => {
    const created = await createExercise(query);
    if (created) {
      resolveExercise(dayIdx, exerciseInternalId, created.id, created.name);
    }
  };

  const unresolvedTotal = preview
    ? preview.days.reduce(
        (acc, d) => acc + d.groups.reduce((a, g) => a + g.exercises.filter((ex) => ex.exercise.id == null).length, 0),
        0
      )
    : 0;

  const totalDays = preview?.days.length ?? 0;

  return (
    <Dialog
      header={intl.formatMessage({ id: 'coach.importCycleAI.title' })}
      visible={visible}
      onHide={handleHide}
      draggable={false}
      resizable={false}
      dismissableMask={!parsing && !committing}
      closable={!parsing && !committing}
      style={{ width: '64rem', maxWidth: '96vw' }}
    >
      {/* Fase 1: upload */}
      {!preview && (
        <div className="flex flex-column gap-3">
          <p className="m-0" style={{ color: 'var(--ios-text-secondary)' }}>
            {intl.formatMessage({ id: 'coach.importCycleAI.intro' })}
          </p>

          <div
            style={{
              background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: 'var(--ios-radius-md)',
              padding: '0.6rem 0.85rem',
              fontSize: '0.85rem',
              color: '#92400e'
            }}
          >
            <i className="pi pi-info-circle" style={{ marginRight: '0.4rem' }} />
            {intl.formatMessage({ id: 'coach.importCycleAI.weeklyWarning' })}
          </div>

          <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
            <TabPanel header={intl.formatMessage({ id: 'coach.importWorkoutAI.tab.file' })}>
              <div className="flex flex-column gap-3">
                <div
                  style={{
                    background: 'var(--ios-surface-subtle)',
                    border: '1px dashed var(--ios-card-border)',
                    borderRadius: 'var(--ios-radius-md)',
                    padding: '1.5rem',
                    textAlign: 'center'
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_MIME_TYPES}
                    onChange={handleFilePicked}
                    style={{ display: 'none' }}
                  />
                  {selectedFile ? (
                    <div className="flex flex-column align-items-center gap-2">
                      <i className="pi pi-file" style={{ fontSize: '1.5rem', color: '#6366f1' }} />
                      <div style={{ fontWeight: 600 }}>{selectedFile.name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--ios-text-secondary)' }}>
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </div>
                      <Button
                        label={intl.formatMessage({ id: 'coach.importWorkoutAI.changeFile' })}
                        icon="pi pi-refresh"
                        className="p-button-text p-button-sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={parsing}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-column align-items-center gap-2">
                      <i
                        className="pi pi-cloud-upload"
                        style={{ fontSize: '2rem', color: 'var(--ios-text-secondary)' }}
                      />
                      <div style={{ fontWeight: 600 }}>
                        {intl.formatMessage({ id: 'coach.importWorkoutAI.dropHint' })}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--ios-text-secondary)' }}>
                        {intl.formatMessage({ id: 'coach.importWorkoutAI.formats' })}
                      </div>
                      <Button
                        label={intl.formatMessage({ id: 'coach.importWorkoutAI.chooseFile' })}
                        icon="pi pi-upload"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={parsing}
                      />
                    </div>
                  )}
                </div>
                <div className="flex justify-content-end gap-2">
                  <Button
                    label={intl.formatMessage({ id: 'common.cancel' })}
                    className="p-button-text"
                    onClick={handleHide}
                    disabled={parsing}
                  />
                  <Button
                    label={intl.formatMessage({ id: 'coach.importCycleAI.parseAction' })}
                    icon={parsing ? undefined : 'pi pi-sparkles'}
                    onClick={handleParseFile}
                    disabled={!selectedFile || parsing}
                    loading={parsing}
                  />
                </div>
              </div>
            </TabPanel>
            <TabPanel header={intl.formatMessage({ id: 'coach.importWorkoutAI.tab.text' })}>
              <div className="flex flex-column gap-3">
                <InputTextarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={12}
                  autoResize
                  placeholder={intl.formatMessage({ id: 'coach.importCycleAI.textPlaceholder' })}
                  disabled={parsing}
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.9rem' }}
                />
                <div className="flex justify-content-end gap-2">
                  <Button
                    label={intl.formatMessage({ id: 'common.cancel' })}
                    className="p-button-text"
                    onClick={handleHide}
                    disabled={parsing}
                  />
                  <Button
                    label={intl.formatMessage({ id: 'coach.importCycleAI.parseAction' })}
                    icon={parsing ? undefined : 'pi pi-sparkles'}
                    onClick={handleParseText}
                    disabled={text.trim().length === 0 || parsing}
                    loading={parsing}
                  />
                </div>
              </div>
            </TabPanel>
          </TabView>

          {parsing && (
            <div
              className="flex align-items-center justify-content-center gap-2"
              style={{ color: 'var(--ios-text-secondary)' }}
            >
              <ProgressSpinner style={{ width: '1.25rem', height: '1.25rem' }} strokeWidth="3" />
              <span style={{ fontSize: '0.9rem' }}>{intl.formatMessage({ id: 'coach.importCycleAI.parsing' })}</span>
            </div>
          )}
        </div>
      )}

      {/* Fase 2: preview */}
      {preview && (
        <div className="flex flex-column gap-3">
          {/* Header con cycle name + weeks */}
          <div className="flex flex-column sm:flex-row gap-2 align-items-end">
            <div className="flex-grow-1">
              <label
                style={{
                  display: 'block',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  marginBottom: '0.3rem',
                  color: 'var(--ios-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}
              >
                {intl.formatMessage({ id: 'coach.importCycleAI.cycleName' })}
              </label>
              <InputText
                className="w-full"
                value={preview.cycleName}
                onChange={(e) => updateCycleName(e.target.value)}
                maxLength={255}
                disabled={committing}
              />
            </div>
            <div style={{ minWidth: '8rem' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  marginBottom: '0.3rem',
                  color: 'var(--ios-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}
              >
                {intl.formatMessage({ id: 'coach.importCycleAI.weeks' })}
              </label>
              <InputNumber
                value={preview.durationWeeks}
                onValueChange={(e) => updateDurationWeeks(e.value ?? 4)}
                min={1}
                max={52}
                showButtons
                disabled={committing}
                inputStyle={{ width: '5rem' }}
              />
            </div>
          </div>

          {/* Aviso de progresión */}
          <div
            style={{
              background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: 'var(--ios-radius-md)',
              padding: '0.5rem 0.75rem',
              fontSize: '0.8rem',
              color: '#92400e'
            }}
          >
            <i className="pi pi-info-circle" style={{ marginRight: '0.4rem' }} />
            {intl.formatMessage({ id: 'coach.importCycleAI.weeklyWarning' })}
          </div>

          {/* Lista de días */}
          <div style={{ maxHeight: '50vh', overflowY: 'auto', paddingRight: '0.25rem' }}>
            {preview.days.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: 'var(--ios-text-secondary)'
                }}
              >
                {intl.formatMessage({ id: 'coach.importCycleAI.empty' })}
              </div>
            ) : (
              preview.days.map((d, idx) => (
                <CycleDayCard
                  key={`day-${idx}-${d.dayNumber}`}
                  day={d}
                  dayIdx={idx}
                  catalogExercises={catalogExercises}
                  onNameChange={updateDayName}
                  onRemove={removeDay}
                  onResolveExercise={resolveExercise}
                  onCreateNewExercise={handleCreateExerciseFromCard}
                />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
            <div style={{ fontSize: '0.85rem', color: 'var(--ios-text-secondary)' }}>
              {totalDays} día(s) ·{' '}
              {unresolvedTotal > 0 ? (
                <span style={{ color: '#ef4444', fontWeight: 600 }}>{unresolvedTotal} ejercicios sin matchear</span>
              ) : (
                <span style={{ color: '#16a34a', fontWeight: 600 }}>Todos los ejercicios matcheados</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                label={intl.formatMessage({ id: 'common.cancel' })}
                className="p-button-text"
                onClick={handleHide}
                disabled={committing}
              />
              <Button
                label={intl.formatMessage({ id: 'coach.importCycleAI.createAction' }, { count: totalDays })}
                icon={committing ? undefined : 'pi pi-check'}
                onClick={handleCommit}
                loading={committing}
                disabled={committing || totalDays === 0 || unresolvedTotal > 0}
              />
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
